<template>
  <div>
    <v-dialog
      :value="value"
      persistent
      @input="$emit('input')"
      :content-class="'create_new_account_modal secondary-artists-dialog'"
      max-width="600"
    >
      <div class="wizard-header">
        <h2 class="heading_wizard">Select Secondary Artist(s)</h2>
        <button class="close_modal" @click="cancel"></button>
      </div>
      
      <!-- Search Box - Styled like the create account box but with search functionality -->
      <div class="box_search" @click.stop="focusSearchInput">
        <div class="search-input-container">
          <input
            ref="searchInput"
            type="text"
            v-model="searchQuery"
            placeholder="Search for artists..."
            :class="{'error_input': filteredArtists.length === 0 && searchQuery.length > 0}"
            @input="searchArtists"
          />
          <button 
            v-if="searchQuery.length > 0" 
            type="button" 
            class="clear_search" 
            @click="clearSearch"
          ></button>
        </div>
      </div>
      
      <!-- Artist Selection Content -->
      <div class="wrapper_content">
        <!-- Always show the list_accounts container -->
        <div class="list_accounts">
          <!-- Loading State -->
          <div v-if="isSearching" class="list-state-container">
            <v-progress-circular :size="40" :width="3" color="#20C4F5" indeterminate></v-progress-circular>
            <span>Searching for artists...</span>
          </div>
          
          <!-- Empty State - No Search -->
          <div v-else-if="!isSearching && filteredArtists.length === 0 && !searchQuery" class="list-state-container">
            <div class="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#7481A6"/>
              </svg>
            </div>
            <span class="empty-message">Select additional artists to feature on this release.</span>
          </div>
          
          <!-- Empty State - No Results -->
          <div v-else-if="!isSearching && filteredArtists.length === 0 && searchQuery.length > 0" class="list-state-container">
            <div class="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#7481A6"/>
              </svg>
            </div>
            <span class="empty-message">No matching Artist accounts found.</span>
            
            <!-- Create button -->
            <button 
              type="button" 
              class="c2a_btn sign_up fizz-effect create-btn" 
              v-ripple 
              @click="createNewArtist"
            >
              Create new artist
            </button>
          </div>
          
          <!-- Artist List Items - Only shown when there are artists to display -->
          <template v-else-if="!isSearching && filteredArtists.length > 0">
            <!-- Primary Artist (if exists) -->
            <div 
              v-if="primaryArtist && !searchQuery" 
              class="item_account primary-artist"
              :class="{'disabled': true}"
            >
              <div class="data">
                <div class="img">
                  <img 
                    v-if="primaryArtist.avatar && primaryArtist.avatar.thumbnail" 
                    :src="`${primaryArtist.avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`"
                    :alt="primaryArtist.name">
                  <div class="default_user_avatar" v-else></div>
                </div>
                <div class="description">
                  <div class="name">{{ primaryArtist.name }} <span class="primary-label">(Primary Artist)</span></div>
                  <div class="type" v-if="primaryArtist.hasActiveSubscription">{{ primaryArtist['@type'] || 'Artist' }}</div>
                  <div v-else class="type restricted">Inactive subscription</div>
                </div>
              </div>
              <div class="action">
                <div class="check_round checked"></div>
              </div>
            </div>
            
            <template v-if="!searchQuery">
              <!-- Top Zone Artists -->
              <div 
                v-for="artist in displayInTopZone" 
                :key="artist.id + '-top'" 
                v-if="!isPrimaryArtist(artist)"
                class="item_account item_account--top-zone"
                @click="toggleArtistSelection(artist)"
              >
                <div class="data">
                  <div class="img">
                    <img 
                      v-if="artist.avatar && artist.avatar.contentUrl" 
                      :src="getAvatarUrl(artist.avatar)"
                      :alt="artist.name">
                    <div class="default_user_avatar" v-else></div>
                  </div>
                  <div class="description">
                    <div class="name">{{ artist.name }}</div>
                    <div class="type" v-if="artist.hasActiveSubscription">{{ artist['@type'] || 'Artist' }}</div>
                    <div v-else class="type restricted">Inactive subscription</div>
                  </div>
                </div>
                <div class="action">
                  <div class="check_box" :class="{'checked': isArtistSelected(artist)}"></div>
                </div>
              </div>

             </template>
            
            <!-- Main List Artists -->
            <div 
              v-for="artist in displayInMainList" 
              :key="artist.id + '-main'" 
              v-if="!isPrimaryArtist(artist)"
              class="item_account item_account--main-list"
              @click="toggleArtistSelection(artist)"
            >
              <div class="data">
                <div class="img">
                  <img 
                    v-if="artist.avatar && artist.avatar.contentUrl" 
                    :src="getAvatarUrl(artist.avatar)"
                    :alt="artist.name">
                  <div class="default_user_avatar" v-else></div>
                </div>
                <div class="description">
                  <div class="name">{{ artist.name }}</div>
                  <div class="type" v-if="artist.hasActiveSubscription">{{ artist['@type'] || 'Artist' }}</div>
                  <div v-else class="type restricted">Inactive subscription</div>
                </div>
              </div>
              <div class="action">
                <div class="check_box" :class="{'checked': isArtistSelected(artist)}"></div>
              </div>
            </div>
          </template>
        </div>
      </div>
      
      <div class="wrapper_button center">
        <button type="button" class="tetriatary_btn" v-ripple @click="cancel">Back</button>
        
        <!-- Standard Select button -->
        <button 
          type="button" 
          class="tetriatary_btn" 
          v-ripple 
          @click="confirmSelection"
          :class="{ 'loading': loading }"
        >
          <v-progress-circular v-if="loading" :width="2" indeterminate></v-progress-circular>
          Next
        </button>
      </div>
    </v-dialog>
    
    <!-- Subscription issue dialog -->
    <v-dialog 
      v-if="accountIssueDialog"
      v-model="accountIssueDialog"
      persistent
      :content-class="'create_new_account_modal'"
      max-width="700"
    >
      <div class="heading">
        <h2 class="heading_new_account">
          Subscription Issue
        </h2>
      </div>
      <div class="wrapper_content h165 v_center">
        <div class="account_issue_box">
          <p>This artist account doesn't have an active subscription.</p>
          <p>Please renew the subscription or choose another artist.</p>
        </div>
      </div>
      <div class="wrapper_button center">
        <button type="button" class="tetriatary_btn" v-ripple @click="accountIssueDialog = false">Back</button>
      </div>
    </v-dialog>

    <!-- New Account Dialog -->
    <new-account
      v-model="showNewAccountDialog"
      :initial-name="searchQuery"
      :uses="'accounts'"
      ref="newAccountComponent"
      @account-created="onAccountCreated"
      @input="onNewAccountDialogChange"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NewAccount from "../newAccount.vue";

export default {
  name: "SecondaryArtistsSelector",
  components: {
    NewAccount
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // Selected secondary artists passed from parent
    selectedArtists: {
      type: Array,
      default() {
        return [];
      }
    },
    // Primary artist passed from parent
    primaryArtist: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      showNewAccountDialog: false,
      accountIssueDialog: false,
      loading: false,
      isSearching: false,
      searchQuery: '',
      filteredArtists: [], // Initialize as empty array
      selectedTrackArtists: [],
      allArtists: [],
      bubbleStyles: {},
      justOpened: false, // Flag to track if the dialog was just opened
      needsSortAfterSearch: false, // Flag to track if we need to sort after search is cleared
      searchJustCleared: false, // Flag to track if search was just cleared
      topZoneArtists: [] // Artists to display in the dedicated top zone
    };
  },
  computed: {
    ...mapGetters([
      'YOUR_ARTISTS',
      'userID',
      'SEARCH_ARTISTS_RESULT',
      'API_URL'
    ]),
    
    displayInTopZone() {
      return this.topZoneArtists;
    },
    
    displayInMainList() {
      if (!this.filteredArtists || this.filteredArtists.length === 0) {
        return [];
      }
      const topZoneIds = new Set(this.topZoneArtists.map(artist => artist.id));
      return this.filteredArtists.filter(artist => !topZoneIds.has(artist.id));
    },
    
    apiUrl() {
      return this.API_URL || 'https://psy.nyc3.digitaloceanspaces.com/public/';
    },
    
    // Combine primary artist and selected secondary artists for the final selection
    allSelectedArtists() {
      // Start with primary artist (if any)
      let artists = [];
      
      // Add primary artist with special flag
      if (this.primaryArtist) {
        artists.push({
          ...this.primaryArtist,
          isAlbumArtist: true,
          isMainArtist: true
        });
      }
      
      // Add selected secondary artists
      if (this.selectedTrackArtists && this.selectedTrackArtists.length) {
        artists = [...artists, ...this.selectedTrackArtists];
      }
      
      return artists;
    },
    
    // Get album artists array for template compatibility
    albumArtists() {
      if (!this.primaryArtist) return [];
      
      return [{
        ...this.primaryArtist,
        isMainArtist: true
      }];
    },
    
    // Combined list of all artists for the unified view
    allArtists() {
      // Return the filtered artists array
      return this.filteredArtists || [];
    },
    
    // Artists list - sorted with selected at top only when dialog first opens or search is cleared
    sortedArtists() {
      console.log('sortedArtists computed property called, justOpened:', this.justOpened, 'searchJustCleared:', this.searchJustCleared);
      
      if (!this.filteredArtists || this.filteredArtists.length === 0) {
        console.log('No filtered artists to sort');
        return [];
      }
      
      // Create a copy of the filtered artists to avoid mutating the original
      const artists = [...this.filteredArtists];
      console.log('Number of artists to sort:', artists.length);
      
      // Only sort if the dialog was just opened OR search was just cleared
      if (this.justOpened || this.searchJustCleared) {
        console.log('Sorting artists with selected at the top (dialog just opened or search just cleared)');
        
        // Log selected artists before sorting
        const selectedArtistIds = this.selectedTrackArtists.map(a => a.id);
        console.log('Selected artist IDs:', selectedArtistIds);
        
        // Sort the artists: selected ones first, then the rest
        const sortedResult = artists.sort((a, b) => {
          const aSelected = this.isArtistSelected(a);
          const bSelected = this.isArtistSelected(b);
          
          if (aSelected && !bSelected) {
            return -1; // a comes before b
          }
          if (!aSelected && bSelected) {
            return 1;  // b comes before a
          }
          return 0; // keep original order
        });
        
        // Log the first few artists after sorting
        if (sortedResult.length > 0) {
          console.log('Sorting complete, first 3 artists after sort:', 
            sortedResult.slice(0, 3).map(a => `${a.name} (${a.id}) - selected: ${this.isArtistSelected(a)}`));
        }
        
        return sortedResult;
      }
      
      // During normal selection, keep the original order
      console.log('Not sorting (normal selection mode)');
      return artists;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // When opening, reset and load artists
        this.searchQuery = '';
        // Set justOpened flag to true when dialog opens and keep it true until closed
        this.justOpened = true;
        // Reinitialize selected artists from prop when dialog opens
        console.log('Dialog opened, selectedArtists prop:', this.selectedArtists);
        this.selectedTrackArtists = this.selectedArtists ? [...this.selectedArtists] : [];
        console.log('Reinitialized selectedTrackArtists:', this.selectedTrackArtists);
        // Populate topZoneArtists with the initial selection
        this.topZoneArtists = [...this.selectedTrackArtists]; 
        console.log('Populated topZoneArtists:', this.topZoneArtists);
        this.loadInitialArtists();
        
        // No timer to reset justOpened - keep it true until dialog closes
      } else {
        // Reset justOpened flag when dialog closes
        this.justOpened = false;
      }
    },
    // Watch for changes in the selectedArtists prop
    selectedArtists: {
      handler(newVal) {
        console.log('selectedArtists prop changed:', newVal);
        this.selectedTrackArtists = newVal ? [...newVal] : [];
        console.log('Updated selectedTrackArtists:', this.selectedTrackArtists);
      },
      deep: true
    }
  },
  created() {
    console.log('SecondaryArtistsSelector created');
    // Initialize selected artists from prop
    this.selectedTrackArtists = this.selectedArtists ? [...this.selectedArtists] : [];
    console.log('Initial selectedTrackArtists:', this.selectedTrackArtists);
    this.loadInitialArtists();
  },
  methods: {
    ...mapActions(['GET_YOUR_ARTISTS', 'SEARCH_ARTISTS']),
    
    isPrimaryArtist(artist) {
      return this.primaryArtist && this.primaryArtist.id === artist.id;
    },
    
    isArtistSelected(artist) {
      // First check if this is the primary artist
      if (this.isPrimaryArtist(artist)) {
        return true;
      }
      
      // Then check if this artist is in the selectedTrackArtists array
      if (!artist || !artist.id) {
        console.warn('isArtistSelected called with invalid artist:', artist);
        return false;
      }
      
      if (!this.selectedTrackArtists || !this.selectedTrackArtists.length) {
        // No selected artists yet
        return false;
      }
      
      // Check each selected artist for a match
      const matchingArtist = this.selectedTrackArtists.find(selectedArtist => selectedArtist.id === artist.id);
      const isSelected = !!matchingArtist;
      
      // Log for debugging - only log when called from sortedArtists to reduce noise
      if (isSelected) {
        console.log(`Artist ${artist.name} (${artist.id}) is selected`);
      }
      
      return isSelected;
    },
    
    toggleArtistSelection(artist) {
      // Don't allow toggling the primary artist
      if (this.isPrimaryArtist(artist)) {
        return;
      }
      
      // Allow selecting artists with expired subscriptions
      // No subscription check here - matching ReleaseInfo.vue behavior
      
      const index = this.selectedTrackArtists.findIndex(a => a.id === artist.id);
      
      if (index === -1) {
        // Artist not selected, add them
        this.selectedTrackArtists.push({
          id: artist.id,
          name: artist.name,
          avatar: artist.avatar || null,
          hasActiveSubscription: artist.hasActiveSubscription // Preserve subscription status
        });
        
        // If we're in search mode, temporarily show selected artists at the top
        // This will ensure that when search is cleared, this artist will appear at the top
        if (this.searchQuery) {
          // Set a flag to remember that we need to sort when search is cleared
          this.needsSortAfterSearch = true;
        }
      } else {
        // Artist already selected, remove them
        this.selectedTrackArtists.splice(index, 1);
      }
    },
    
    getBubbleStyle(index) {
      if (!this.bubbleStyles[index]) {
        // Random parameters for each bubble
        const leftPos = 5 + Math.floor(Math.random() * 85); // 5-90%
        const animDuration = 1.5 + (Math.random() * 1.5); // 1.5-3s
        const animDelay = -(Math.random() * 2); // -0 to -2s
        
        this.bubbleStyles[index] = {
          left: `${leftPos}%`,
          animation: `floatUp ${animDuration}s infinite linear`,
          animationDelay: `${animDelay}s`
        };
      }
      
      return this.bubbleStyles[index];
    },
    
    resetBubbleStyles() {
      this.bubbleStyles = {};
    },
    
    clearSearch() {
      console.log('Clear search button clicked');
      console.log('Current selected artists:', this.selectedTrackArtists);
      
      // Update topZoneArtists with the current selection when search is cleared
      this.topZoneArtists = [...this.selectedTrackArtists];
      console.log('Updated topZoneArtists on clearSearch:', this.topZoneArtists);
       
      // Store selected artists before clearing search
      const selectedArtists = [...this.selectedTrackArtists];
      console.log('Stored selected artists:', selectedArtists.map(a => `${a.name} (${a.id})`));
      
      // Set the searchJustCleared flag to true before clearing the search
      this.searchJustCleared = true;
      console.log('Set searchJustCleared to true');
      
      this.searchQuery = '';
      console.log('Search query cleared');
      
      // Use setTimeout to ensure Vue has completely processed the search clearing
      setTimeout(() => {
        console.log('Timeout fired, now sorting artists');
        
        // Get the selected artist IDs
        const selectedIds = selectedArtists.map(a => a.id);
        console.log('Selected artist IDs:', selectedIds);
        
        // Create a new sorted array with selected artists at the top
        const allArtists = [...this.YOUR_ARTISTS]; // Make a fresh copy
        
        // Filter out the primary artist if it exists
        const filteredArtists = this.primaryArtist ? 
          allArtists.filter(artist => artist.id !== this.primaryArtist.id) : 
          allArtists;
        
        // Check if all selected artists are in the filtered results
        // This is important because some selected artists might not be in the current page of results
        const missingSelectedArtists = [];
        selectedArtists.forEach(selectedArtist => {
          // Check if this selected artist is in the filtered results
          const isInFiltered = filteredArtists.some(artist => artist.id === selectedArtist.id);
          if (!isInFiltered) {
            console.log(`Selected artist ${selectedArtist.name} (${selectedArtist.id}) is not in filtered results, adding it`);
            missingSelectedArtists.push(selectedArtist);
          }
        });
        
        // Completely replace the filteredArtists array
        this.filteredArtists = [];
        this.$nextTick(() => {
          if (selectedIds.length === 0) {
            // If no artists were selected, just show all filtered artists
            console.log('No selected artists to sort, showing all artists');
            this.filteredArtists = filteredArtists;
          } else {
            // Get all selected artists that are in the filtered results
            const selected = filteredArtists.filter(a => selectedIds.includes(a.id));
            // Add any missing selected artists
            const allSelected = [...selected, ...missingSelectedArtists];
            const unselected = filteredArtists.filter(a => !selectedIds.includes(a.id));
            // Now assign the new sorted array with all selected artists at the top
            this.filteredArtists = [...allSelected, ...unselected];
            console.log(`Found ${selected.length} selected artists in filtered results, added ${missingSelectedArtists.length} missing selected artists`);
          }
          
          console.log('Forced sort completed, selected artists should be at the top');
          console.log('First 3 artists after sort:', 
            this.filteredArtists.slice(0, 3).map(a => `${a.name} (${a.id}) - selected: ${this.isArtistSelected(a)}`));
          
          // Reset scroll position to the top
          this.$nextTick(() => {
            const listElement = document.querySelector('.list_accounts');
            if (listElement) {
              console.log('Scrolling list to top');
              listElement.scrollTop = 0;
            }
          });
        });
      }, 50); // Short delay to ensure Vue has processed the search clearing
      
      this.resetBubbleStyles();
      
      // Reset the searchJustCleared flag after a short delay
      setTimeout(() => {
        this.searchJustCleared = false;
        console.log('Reset searchJustCleared to false');
      }, 500);
      
      console.log('clearSearch method completed');
    },
    
    resetSearchJustCleared() {
      console.log('Resetting searchJustCleared flag');
      this.searchJustCleared = false;
    },
    
    openNewAccountDialog() {
      this.showNewAccountDialog = true;
    },
    
    createNewArtist() {
      this.showNewAccountDialog = true;
      
      // Use nextTick to ensure the component is mounted before accessing it
      this.$nextTick(() => {
        if (this.$refs.newAccountComponent) {
          // Set artist account type directly
          this.$refs.newAccountComponent.selectedTypeAccount = 'Artist';
          // Skip to subscription selection step
          this.$refs.newAccountComponent.changeStep(2);
          
          // Override the cancel method
          const originalCancel = this.$refs.newAccountComponent.cancel;
          this.$refs.newAccountComponent.cancel = () => {
            this.showNewAccountDialog = false;
          };
          
          // Override the back button behavior in step 2
          const originalChangeStep = this.$refs.newAccountComponent.changeStep;
          this.$refs.newAccountComponent.changeStep = (step) => {
            // If trying to go to step 1 (account type selection) from step 2 (subscription)
            if (step === 1 && this.$refs.newAccountComponent.step === 2) {
              // Close the new account dialog and return to the secondary artists selector
              this.showNewAccountDialog = false;
            } else {
              // Otherwise use the original method
              originalChangeStep.call(this.$refs.newAccountComponent, step);
            }
          };
        }
      });
    },
    
    loadInitialArtists() {
      this.isSearching = true;
      console.log('Loading artists for secondary artist selection...');
      
      // Get all available artists without filtering by 'mine'
      this.GET_YOUR_ARTISTS({})
        .then(() => {
          console.log('GET_YOUR_ARTISTS response:', this.YOUR_ARTISTS);
          
          if (this.YOUR_ARTISTS && this.YOUR_ARTISTS.length > 0) {
            // Filter out the primary artist if it exists
            this.filteredArtists = this.primaryArtist ? 
              this.YOUR_ARTISTS.filter(artist => artist.id !== this.primaryArtist.id) : 
              this.YOUR_ARTISTS;
              
            console.log('Loaded artists for secondary artist selection:', this.filteredArtists.length);
            console.log('First 3 artists:', this.filteredArtists.slice(0, 3));
          } else {
            console.log('No artists found or YOUR_ARTISTS is undefined');
            this.filteredArtists = [];
          }
        })
        .catch(err => {
          console.error('Error loading artists:', err);
          this.filteredArtists = [];
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    
    async searchArtists() {
      if (!this.searchQuery) {
        // If search query is empty, reload all artists
        this.loadInitialArtists();
        return;
      }
      
      this.isSearching = true;
      // Reset the sort flag when starting a new search
      this.needsSortAfterSearch = false;
      console.log('Searching for artists with query:', this.searchQuery);
      
      try {
        // Search all artists, not just the user's own
        await this.SEARCH_ARTISTS({ name: this.searchQuery });
        
        if (this.SEARCH_ARTISTS_RESULT) {
          // Filter out the primary artist from search results
          this.filteredArtists = this.primaryArtist ? 
            this.SEARCH_ARTISTS_RESULT.filter(artist => artist.id !== this.primaryArtist.id) : 
            this.SEARCH_ARTISTS_RESULT;
            
          console.log('Found artists matching search:', this.filteredArtists.length);
          
          // Ensure artists maintain their original order when searching
          this.justOpened = false;
        }
      } catch (error) {
        console.error('Error searching artists:', error);
      } finally {
        this.isSearching = false;
      }
    },
    
    confirmSelection() {
      this.loading = true;
      
      // Emit the selected artists back to the parent component
      this.$emit('selection-confirmed', this.selectedTrackArtists);
      
      // Use nextTick to ensure the events are processed before resetting loading state
      this.$nextTick(() => {
        // Reset loading state
        this.loading = false;
        // Close the dialog
        this.$emit('input', false);
      });
    },
    
    cancel() {
      // Use a single event to close the dialog and unpause the wizard
      // This prevents potential event loops
      this.$emit('dialog-closed');
      // Use nextTick to avoid potential race conditions
      this.$nextTick(() => {
        this.$emit('input', false);
      });
    },
    
    getAvatarUrl(avatar) {
      if (!avatar || !avatar.contentUrl) return null;
      
      try {
        // First try to use thumbnail if available
        if (avatar.thumbnail && avatar.thumbnail['50x50']) {
          return `${avatar.thumbnail['50x50']}?cache=${new Date().getTime()}`;
        }
        
        // Otherwise use content URL
        const contentUrl = avatar.contentUrl;
        
        // Handle different URL formats
        let fullUrl;
        if (contentUrl.startsWith('http')) {
          // Already a full URL
          fullUrl = contentUrl;
        } else if (contentUrl.startsWith('/')) {
          // Relative URL starting with /
          fullUrl = `${this.apiUrl}${contentUrl.slice(1)}`;
        } else {
          // Other relative URL
          fullUrl = `${this.apiUrl}${contentUrl}`;
        }
        
        return `${fullUrl}?cache=${new Date().getTime()}`;
      } catch (error) {
        console.error('Error generating avatar URL:', error);
        return null;
      }
    },
    
    focusSearchInput(event) {
      // Only focus if the click wasn't directly on the input or clear button
      // This prevents double-focus events when clicking directly on the input
      if (event && event.target && event.target.tagName !== 'INPUT' && 
          !event.target.classList.contains('clear_search') && 
          this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    
    onAccountCreated(newArtist) {
      this.showNewAccountDialog = false;
      
      if (newArtist) {
        // Add the new artist to the selected artists
        this.selectedTrackArtists.push(newArtist);
        
        // Also add to filtered artists if not already there
        if (!this.filteredArtists.some(artist => artist.id === newArtist.id)) {
          this.filteredArtists.push(newArtist);
        }
      }
    },
    
    onNewAccountDialogChange(value) {
      this.showNewAccountDialog = value;
      if (!value) {
        // When new account dialog is closed, show the select dialog again
        this.showSelectDialog = true;
      }
    }
  },
  created() {
    console.log(`TrackArtistSelector created for track index: ${this.trackIndex}, title: ${this.trackTitle}`);
    this.loadInitialArtists();
  }
}
</script>

<style lang="scss">
/* Component-specific styles for SecondaryArtistsSelector */

.search-container {
  width: 100%;
  padding: 10px 0;
}

/* Search box styled like the create account box */
.box_search {
  position: relative;
  margin: 0 20px 20px;
  padding: 15px;
  background: rgba(32, 196, 245, 0.05);
  border: 1px solid rgba(32, 196, 245, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  width: calc(100% - 40px); /* Match the width of the selection area (100% - left margin - right margin) */
  box-sizing: border-box; /* Ensure padding is included in the width calculation */
  
  &:hover {
    background: rgba(32, 196, 245, 0.1);
  }
  
  .search-input-container {
    position: relative;
    width: 100%;
  }
  
  input {
    width: 100%;
    padding: 12px 15px 12px 40px;
    background: transparent;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    color: #fff;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }
    
    &:focus {
      outline: none;
    }
    
    &.error_input {
      border: 1px solid rgba(233, 78, 78, 0.5);
      background: rgba(233, 78, 78, 0.1);
    }
  }
  
  /* Search icon */
  .search-input-container:before {
    content: '';
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>') no-repeat center;
    background-size: contain;
  }
  
  .clear_search {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>') no-repeat center;
    background-size: 20px;
    border: none;
    cursor: pointer;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
  }
}

.section-heading {
  font-size: 14px;
  font-weight: 500;
  color: #7481A6;
  padding: 10px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.track-artists-heading {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

/* Styles for the wizard header */
.wizard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  margin-bottom: 10px;
  
  .heading_wizard {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: #ffffff;
    text-align: center;
    flex-grow: 1;
    padding-bottom: 0;
    line-height: 28px;
  }
  
  .close_modal {
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: none;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#7481A6"/></svg>') no-repeat center;
    background-size: 20px;
    
    &:hover {
      opacity: 1;
    }
  }
}

/* Custom styles for the secondary artists dialog */
.secondary-artists-dialog {
  display: flex;
  flex-direction: column;
  
  .wizard-header,
  .box_search,
  .wrapper_button {
    flex-shrink: 0;
  }
  
  .wrapper_content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 0 20px 20px;
    
    .list_accounts {
      flex-grow: 1;
      background: #0C1324;
      border: 1px solid #282C47;
      border-radius: 10px;
      margin-bottom: 20px;
      height: 350px; /* Fixed height */
      overflow-y: auto;
      overscroll-behavior: contain; /* Prevents scroll chaining to parent elements */
      -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    }
    
    /* Make sure empty states and loading states maintain height */
    .list-loading-container,
    .empty-results-container,
    .list-state-container {
      min-height: 300px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 40px 20px;
      text-align: center;
      
      .empty-icon {
        margin-bottom: 20px;
      }
      
      .empty-message {
        font-size: 16px;
        color: #7481A6;
        margin-bottom: 25px;
      }
      
      .create-btn {
        margin-top: 10px;
      }
    }
  }
  
  /* Style the empty results container to match the artist list */
  .empty-results-container {
    background: #0C1324;
    border: 1px solid #282C47;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;
    
    .empty-state-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      height: 100%;
      width: 100%;
    }
    
    .empty-icon {
      margin-bottom: 20px;
    }
    
    .empty-message {
      font-size: 16px;
      color: #7481A6;
      margin-bottom: 25px;
      text-align: center;
    }
    
    .create-btn {
      margin-top: 10px;
    }
  }
}

.album-artists-section,
.create_account_box {
  flex: 1;
  min-width: 0; /* Prevent flex items from overflowing */
}

.album-artists-section {
  background-color: rgba(32, 196, 245, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(32, 196, 245, 0.2);
  padding: 15px;
  height: fit-content;

  .section-heading {
    font-weight: 600;
    margin-bottom: 10px;
    color: #20C4F5;
  }

  .album-artists-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .main-artist {
    border-left: 3px solid #20C4F5;
    background-color: rgba(32, 196, 245, 0.1);
    
    .type {
      color: #20C4F5;
    }
  }
  
  .secondary-artist {
    border-left: 3px solid #9c27b0;
    background-color: rgba(156, 39, 176, 0.05);
  }
  
  .artist-role {
    font-size: 0.8em;
    opacity: 0.7;
    margin-left: 5px;
    font-style: italic;
  }
}

.fixed-height {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #282C47;
  border-radius: 5px;
  padding: 0;
}

.item_account {
  position: relative;
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: rgba(24, 32, 52, 0.5) !important; /* Force the same color as primary artist selection */
  }
  
  &.disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &.primary-artist {
    background-color: rgba(32, 196, 245, 0.05);
    border-left: 3px solid #20C4F5;
    margin-bottom: 10px;
    
    .primary-label {
      font-size: 12px;
      color: #20C4F5;
      font-weight: normal;
      margin-left: 5px;
    }
  }
  
  /* No special styling for selected items - only using checkmark */
  
  .data {
    display: flex;
    align-items: center;
    flex-grow: 1;
    
    .img {
      width: 40px;
      height: 40px;
      border-radius: 10px; /* Changed from 50% to 10px for more square-like appearance */
      overflow: hidden;
      margin-right: 10px; /* Reduced from 15px to bring text closer */
      flex-shrink: 0;
      background-color: #5F6986;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      .default_user_avatar {
        width: 100%;
        height: 100%;
        border-radius: 10px; /* Match parent container */
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="%237481A6"/></svg>') no-repeat center;
        background-size: 24px;
      }
    }
    
    .description {
      flex-grow: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center; /* Center content vertically */
      height: 40px; /* Match avatar height for vertical centering */
      margin-bottom: 0 !important; /* Force remove any margin-bottom */
      
      .name {
        font-size: 16px;
        font-weight: 500;
        color: #ffffff;
        margin-bottom: 0 !important; /* Force remove any margin-bottom */
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left; /* Ensure left alignment */
        line-height: 1.2; /* Tighter line height */
      }
      
      .type {
        font-size: 14px;
        color: #7481A6;
        text-align: left; /* Ensure left alignment */
        line-height: 1.2; /* Tighter line height */
        margin-top: 2px; /* Small space between name and type */
      }
      
      .inactive {
        color: #E94E4E;
      }
    }
  }

.check_box {
  width: 22px;
  height: 22px;
  border: 1px solid #7481A6;
  border-radius: 3px;
  position: relative;
  transition: all 0.2s ease;
  
  &.checked {
    background-color: #20C4F5;
    border-color: #20C4F5;
    
    &:after {
      content: '';
      position: absolute;
      width: 6px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      top: 5px;
      left: 8px;
      transform: rotate(45deg);
    }
  }
}

/* Add styling for the action area to match primary artist selection */
.action {
  margin-left: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-input-wrapper {
  position: relative;
  width: 100%;
  
  &:after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z' fill='%23dcdcdc'/%3e%3cpath d='M11.4121 8.58609C11.7911 8.96609 12.0001 9.46809 12.0001 10.0001H14.0001C14.001 9.47451 13.8977 8.95398 13.6961 8.46857C13.4946 7.98316 13.1989 7.54251 12.8261 7.17209C11.3121 5.66009 8.68707 5.66009 7.17407 7.17209L8.58607 8.58809C9.34607 7.83009 10.6561 7.83209 11.4121 8.58609Z' fill='%23dcdcdc'/%3e%3c/svg%3e");
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    opacity: 0.8;
    z-index: 1;
  }
  
  input {
    width: 100%;
    height: 52px;
    background: #0C1324;
    border: 1px solid #282C47;
    border-radius: 5px;
    overflow: hidden;
    color: #fff;
    font-size: 16px;
    padding: 0 45px 0 40px;
    transition: color 0.2s ease, border-color 0.2s ease;
    
    &.error_name {
      border: 1px solid #E94E4E;
    }
    
    &::placeholder {
      color: #7481A6;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }
    
    &:focus {
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.5);
      &::placeholder {
        opacity: 0.5;
      }
    }
  }
  
  .clear_search {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 15px;
    width: 15px;
    height: 15px;
    background-position: center;
    background-size: contain;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='18' height='19' viewBox='0 0 18 19' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M1.54722 17.9619L16.6417 1.97953M16.6417 17.9619L1.54722 1.97953' stroke='%23dcdcdc' stroke-width='2' stroke-linecap='round'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    opacity: 0.8;
    transition: opacity 0.2s ease;
    background-color: transparent;
    border: none;
    cursor: pointer;
    
    &::before {
      content: '';
      position: absolute;
      top: -15px;
      right: -15px;
      bottom: -15px;
      left: -15px;
    }
    
    &:hover {
      opacity: 1;
    }
  }
}

.list-loading-container {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  color: #7481A6;
  gap: 10px;
  text-align: center;
  background: #0C1324;
  border: 1px solid #282C47;
  border-radius: 5px;
  margin-bottom: 15px;
}

.list_accounts {
  max-height: 330px;
  overflow-y: auto;
  border: 1px solid #282C47;
  border-radius: 5px;
  background-color: #0C1324;
  margin-bottom: 15px;
  overscroll-behavior: contain; /* Prevents scroll chaining to parent elements */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.empty-icon {
  opacity: 0.6;
}

.c2a_btn {
  width: auto;
  min-width: 120px;
  height: 55px;
  border-radius: 50px;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &.sign_up {
    background: rgba(0, 200, 100, 0.9) !important;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    
    &:hover {
      background: rgba(0, 210, 110, 0.95) !important;
    }
  }
  
  &.create-btn {
    margin-top: 20px;
    height: 45px;
    font-size: 15px;
    max-width: 200px;
  }
}

.fizz-effect {
  position: relative;
  overflow: hidden;

  // Bubbles container
  &:after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 200px;
    bottom: 100%;
    pointer-events: none;
  }

  // Multiple bubble streams
  .bubble-stream {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    opacity: 0.6;
    bottom: 0;
    /* Individual bubble styles now applied inline */
  }
}
}

@keyframes floatUp {
  0% {
    transform: translateY(100px) scale(0.5);
  }
  100% {
    transform: translateY(-100px) scale(1.3);
  }
}
</style> 